// Excel-only logo sizing. The PDF path keeps loadImageAsDataUrl unchanged.
// Raw deflate inflate is adapted from foliojs/tiny-inflate (MIT). ExcelJS
// cannot place an in-cell picture, so the workbook embeds a floating PNG
// cropped to its content and drawn at the source aspect ratio.

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];
const LOGO_TARGET_HEIGHT = 170;
const MARGIN_ALPHA = 16;
const MARGIN_WHITE = 250;
// Reject a decode that would allocate a huge bitmap. Canvas logos are ~320px.
const MAX_PNG_EDGE = 1024;
const MAX_PNG_PIXELS = 1024 * 1024;

const readU32 = (bytes, offset) => (
  ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0
);

const bytesFromBase64 = (base64) => {
  const clean = String(base64 || '').replace(/\s/g, '');
  if (typeof atob === 'function') {
    const binary = atob(clean);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  }
  return Uint8Array.from(Buffer.from(clean, 'base64'));
};

const bytesToBase64 = (bytes) => {
  if (typeof btoa !== 'function') return Buffer.from(bytes).toString('base64');
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
};

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let crc = index;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 1) ? ((0xEDB88320 ^ (crc >>> 1)) >>> 0) : (crc >>> 1);
    }
    table[index] = crc >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xFFFFFFFF;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = (crcTable[(crc ^ bytes[index]) & 255] ^ (crc >>> 8)) >>> 0;
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
};

const adler32 = (bytes) => {
  let a = 1;
  let b = 0;
  for (let index = 0; index < bytes.length; index += 1) {
    a = (a + bytes[index]) % 65521;
    b = (b + a) % 65521;
  }
  return ((b << 16) | a) >>> 0;
};

const Tree = function Tree() {
  this.table = new Uint16Array(16);
  this.trans = new Uint16Array(288);
};

const lengthBits = new Uint8Array(30);
const lengthBase = new Uint16Array(30);
const distBits = new Uint8Array(30);
const distBase = new Uint16Array(30);
const clcIndex = new Uint8Array([
  16, 17, 18, 0, 8, 7, 9, 6,
  10, 5, 11, 4, 12, 3, 13, 2,
  14, 1, 15
]);
const codeTree = new Tree();
const codeLengths = new Uint8Array(288 + 32);
const lengthOffsets = new Uint16Array(16);
const fixedLengthTree = new Tree();
const fixedDistTree = new Tree();

const buildBitsBase = (bits, base, delta, first) => {
  let index = 0;
  for (index = 0; index < delta; index += 1) bits[index] = 0;
  for (index = 0; index < 30 - delta; index += 1) bits[index + delta] = Math.floor(index / delta);
  let sum = first;
  for (index = 0; index < 30; index += 1) {
    base[index] = sum;
    sum += 1 << bits[index];
  }
};

const buildTree = (tree, lengths, offset, count) => {
  let index = 0;
  for (index = 0; index < 16; index += 1) tree.table[index] = 0;
  for (index = 0; index < count; index += 1) tree.table[lengths[offset + index]] += 1;
  tree.table[0] = 0;
  let sum = 0;
  for (index = 0; index < 16; index += 1) {
    lengthOffsets[index] = sum;
    sum += tree.table[index];
  }
  for (index = 0; index < count; index += 1) {
    const length = lengths[offset + index];
    if (length) {
      tree.trans[lengthOffsets[length]] = index;
      lengthOffsets[length] += 1;
    }
  }
};

const buildFixedTrees = () => {
  let index = 0;
  for (index = 0; index < 7; index += 1) fixedLengthTree.table[index] = 0;
  fixedLengthTree.table[7] = 24;
  fixedLengthTree.table[8] = 152;
  fixedLengthTree.table[9] = 112;
  for (index = 0; index < 24; index += 1) fixedLengthTree.trans[index] = 256 + index;
  for (index = 0; index < 144; index += 1) fixedLengthTree.trans[24 + index] = index;
  for (index = 0; index < 8; index += 1) fixedLengthTree.trans[24 + 144 + index] = 280 + index;
  for (index = 0; index < 112; index += 1) fixedLengthTree.trans[24 + 144 + 8 + index] = 144 + index;
  for (index = 0; index < 5; index += 1) fixedDistTree.table[index] = 0;
  fixedDistTree.table[5] = 32;
  for (index = 0; index < 32; index += 1) fixedDistTree.trans[index] = index;
};

buildBitsBase(lengthBits, lengthBase, 4, 3);
buildBitsBase(distBits, distBase, 2, 1);
lengthBits[28] = 0;
lengthBase[28] = 258;
buildFixedTrees();

const bitReader = (source) => ({
  source,
  sourceIndex: 0,
  tag: 0,
  bitCount: 0
});

const pullByte = (reader) => {
  const value = reader.source[reader.sourceIndex] || 0;
  reader.sourceIndex += 1;
  return value;
};

const readBit = (reader) => {
  if (!reader.bitCount) {
    reader.tag = pullByte(reader);
    reader.bitCount = 8;
  }
  const bit = reader.tag & 1;
  reader.tag >>>= 1;
  reader.bitCount -= 1;
  return bit;
};

const fillTag = (reader) => {
  while (reader.bitCount < 24) {
    reader.tag |= pullByte(reader) << reader.bitCount;
    reader.bitCount += 8;
  }
};

const readBits = (reader, count, base) => {
  if (!count) return base;
  fillTag(reader);
  const mask = 0xFFFF >>> (16 - count);
  const value = reader.tag & mask;
  reader.tag >>>= count;
  reader.bitCount -= count;
  return value + base;
};

const decodeSymbol = (reader, tree) => {
  fillTag(reader);
  let sum = 0;
  let current = 0;
  let length = 0;
  let tag = reader.tag;
  do {
    current = (2 * current) + (tag & 1);
    tag >>>= 1;
    length += 1;
    sum += tree.table[length];
    current -= tree.table[length];
  } while (current >= 0);
  reader.tag = tag;
  reader.bitCount -= length;
  return tree.trans[sum + current];
};

const decodeTrees = (reader, lengthTree, distanceTree) => {
  const hlit = readBits(reader, 5, 257);
  const hdist = readBits(reader, 5, 1);
  const hclen = readBits(reader, 4, 4);
  codeLengths.fill(0);
  for (let index = 0; index < hclen; index += 1) {
    codeLengths[clcIndex[index]] = readBits(reader, 3, 0);
  }
  buildTree(codeTree, codeLengths, 0, 19);
  for (let count = 0; count < hlit + hdist;) {
    const symbol = decodeSymbol(reader, codeTree);
    if (symbol === 16) {
      const previous = codeLengths[count - 1];
      const repeat = readBits(reader, 2, 3);
      for (let copy = 0; copy < repeat; copy += 1) {
        codeLengths[count] = previous;
        count += 1;
      }
    } else if (symbol === 17 || symbol === 18) {
      const repeat = readBits(reader, symbol === 17 ? 3 : 7, symbol === 17 ? 3 : 11);
      for (let copy = 0; copy < repeat; copy += 1) {
        codeLengths[count] = 0;
        count += 1;
      }
    } else {
      codeLengths[count] = symbol;
      count += 1;
    }
  }
  buildTree(lengthTree, codeLengths, 0, hlit);
  buildTree(distanceTree, codeLengths, hlit, hdist);
};

const inflateBlock = (reader, dest, lengthTree, distanceTree) => {
  let symbol = decodeSymbol(reader, lengthTree);
  while (symbol !== 256) {
    if (symbol < 256) {
      dest[reader.destLen] = symbol;
      reader.destLen += 1;
    } else {
      const length = readBits(reader, lengthBits[symbol - 257], lengthBase[symbol - 257]);
      const distanceSymbol = decodeSymbol(reader, distanceTree);
      const distance = readBits(reader, distBits[distanceSymbol], distBase[distanceSymbol]);
      const start = reader.destLen - distance;
      for (let index = 0; index < length; index += 1) {
        dest[reader.destLen] = dest[start + index];
        reader.destLen += 1;
      }
    }
    symbol = decodeSymbol(reader, lengthTree);
  }
};

const inflateStored = (reader, dest) => {
  // bitCount === 8 is a whole unused byte that was pulled ahead. Leaving it
  // makes the stored-block length disagree with its complement ('Bad stored
  // block'), which is how Chrome-canvas PNGs were skipping the crop.
  while (reader.bitCount >= 8) {
    reader.sourceIndex -= 1;
    reader.bitCount -= 8;
  }
  const length = reader.source[reader.sourceIndex] + (reader.source[reader.sourceIndex + 1] * 256);
  const inverse = reader.source[reader.sourceIndex + 2] + (reader.source[reader.sourceIndex + 3] * 256);
  if (length !== ((~inverse) & 0xFFFF)) throw new Error('Bad stored block');
  reader.sourceIndex += 4;
  for (let index = 0; index < length; index += 1) {
    dest[reader.destLen] = reader.source[reader.sourceIndex];
    reader.destLen += 1;
    reader.sourceIndex += 1;
  }
  reader.bitCount = 0;
};

const inflateRaw = (source, dest) => {
  const reader = bitReader(source);
  reader.destLen = 0;
  let finished = false;
  while (!finished) {
    finished = readBit(reader) === 1;
    const type = readBits(reader, 2, 0);
    if (type === 0) inflateStored(reader, dest);
    else if (type === 1) inflateBlock(reader, dest, fixedLengthTree, fixedDistTree);
    else if (type === 2) {
      const lengthTree = new Tree();
      const distanceTree = new Tree();
      decodeTrees(reader, lengthTree, distanceTree);
      inflateBlock(reader, dest, lengthTree, distanceTree);
    } else {
      throw new Error('Bad deflate block');
    }
  }
  return dest.subarray(0, reader.destLen);
};

const inflateZlib = (bytes, size) => {
  if (!bytes || bytes.length < 6) throw new Error('Short zlib stream');
  const cmf = bytes[0];
  const flg = bytes[1];
  if ((cmf & 0x0F) !== 8 || (((cmf << 8) + flg) % 31) !== 0) throw new Error('Bad zlib header');
  let start = 2;
  if (flg & 32) start += 4;
  const payload = bytes.subarray(start, bytes.length - 4);
  const inflated = inflateRaw(payload, new Uint8Array(size));
  if (inflated.length !== size) throw new Error('Unexpected inflate size');
  return inflated;
};

const paeth = (left, up, upLeft) => {
  const estimate = left + up - upLeft;
  const leftDistance = Math.abs(estimate - left);
  const upDistance = Math.abs(estimate - up);
  const diagonal = Math.abs(estimate - upLeft);
  if (leftDistance <= upDistance && leftDistance <= diagonal) return left;
  if (upDistance <= diagonal) return up;
  return upLeft;
};

const unfilter = (raw, height, stride, bpp) => {
  const out = new Uint8Array(height * stride);
  const rowLength = stride + 1;
  for (let y = 0; y < height; y += 1) {
    const filter = raw[(y * rowLength)];
    const src = (y * rowLength) + 1;
    const dst = y * stride;
    for (let x = 0; x < stride; x += 1) {
      const left = x >= bpp ? out[dst + x - bpp] : 0;
      const up = y > 0 ? out[dst + x - stride] : 0;
      const upLeft = y > 0 && x >= bpp ? out[dst + x - stride - bpp] : 0;
      const byte = raw[src + x];
      let value = byte;
      if (filter === 1) value = (byte + left) & 255;
      else if (filter === 2) value = (byte + up) & 255;
      else if (filter === 3) value = (byte + Math.floor((left + up) / 2)) & 255;
      else if (filter === 4) value = (byte + paeth(left, up, upLeft)) & 255;
      else if (filter !== 0) throw new Error('Bad PNG filter');
      out[dst + x] = value;
    }
  }
  return out;
};

const channelsFor = (colorType) => {
  if (colorType === 0) return 1;
  if (colorType === 2) return 3;
  if (colorType === 3) return 1;
  if (colorType === 4) return 2;
  if (colorType === 6) return 4;
  return 0;
};

const toRgba = (pixels, width, height, colorType, palette, transparency) => {
  const rgba = new Uint8Array(width * height * 4);
  const channels = channelsFor(colorType);
  for (let index = 0; index < width * height; index += 1) {
    const src = index * channels;
    const dst = index * 4;
    if (colorType === 6) {
      rgba[dst] = pixels[src];
      rgba[dst + 1] = pixels[src + 1];
      rgba[dst + 2] = pixels[src + 2];
      rgba[dst + 3] = pixels[src + 3];
    } else if (colorType === 2) {
      rgba[dst] = pixels[src];
      rgba[dst + 1] = pixels[src + 1];
      rgba[dst + 2] = pixels[src + 2];
      rgba[dst + 3] = 255;
    } else if (colorType === 0) {
      rgba[dst] = pixels[src];
      rgba[dst + 1] = pixels[src];
      rgba[dst + 2] = pixels[src];
      rgba[dst + 3] = 255;
    } else if (colorType === 4) {
      rgba[dst] = pixels[src];
      rgba[dst + 1] = pixels[src];
      rgba[dst + 2] = pixels[src];
      rgba[dst + 3] = pixels[src + 1];
    } else if (colorType === 3) {
      const entry = pixels[src] * 3;
      rgba[dst] = palette[entry] || 0;
      rgba[dst + 1] = palette[entry + 1] || 0;
      rgba[dst + 2] = palette[entry + 2] || 0;
      rgba[dst + 3] = transparency && transparency.length > pixels[src] ? transparency[pixels[src]] : 255;
    }
  }
  return rgba;
};

const parsePng = (bytes) => {
  if (!bytes || bytes.length < 29) throw new Error('Short PNG');
  for (let index = 0; index < PNG_SIGNATURE.length; index += 1) {
    if (bytes[index] !== PNG_SIGNATURE[index]) throw new Error('Not a PNG');
  }
  const width = readU32(bytes, 16);
  const height = readU32(bytes, 20);
  const bitDepth = bytes[24];
  const colorType = bytes[25];
  const interlace = bytes[28];
  const channels = channelsFor(colorType);
  if (!width || !height || bitDepth !== 8 || !channels || interlace !== 0) {
    throw new Error('Unsupported PNG');
  }
  if (width > MAX_PNG_EDGE || height > MAX_PNG_EDGE || width * height > MAX_PNG_PIXELS) {
    throw new Error('PNG is too large');
  }
  const stride = width * channels;
  const rawSize = height * (stride + 1);
  if (!Number.isSafeInteger(rawSize) || rawSize > MAX_PNG_PIXELS * 5) {
    throw new Error('PNG is too large');
  }
  const idatParts = [];
  let palette = null;
  let transparency = null;
  let offset = 8;
  while (offset + 8 <= bytes.length) {
    const length = readU32(bytes, offset);
    const type = String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7]);
    const data = bytes.subarray(offset + 8, offset + 8 + length);
    if (type === 'IDAT') idatParts.push(data);
    else if (type === 'PLTE') palette = data;
    else if (type === 'tRNS') transparency = data;
    else if (type === 'IEND') break;
    offset += 12 + length;
  }
  let total = 0;
  idatParts.forEach((part) => {
    total += part.length;
  });
  const idat = new Uint8Array(total);
  let cursor = 0;
  idatParts.forEach((part) => {
    idat.set(part, cursor);
    cursor += part.length;
  });
  const raw = inflateZlib(idat, rawSize);
  const pixels = unfilter(raw, height, stride, channels);
  return {
    width,
    height,
    rgba: toRgba(pixels, width, height, colorType, palette, transparency)
  };
};

const pngChunk = (type, data) => {
  const body = new Uint8Array(4 + data.length);
  body[0] = type.charCodeAt(0);
  body[1] = type.charCodeAt(1);
  body[2] = type.charCodeAt(2);
  body[3] = type.charCodeAt(3);
  body.set(data, 4);
  const crc = crc32(body);
  const out = new Uint8Array(12 + data.length);
  const length = data.length;
  out[0] = (length >>> 24) & 255;
  out[1] = (length >>> 16) & 255;
  out[2] = (length >>> 8) & 255;
  out[3] = length & 255;
  out.set(body, 4);
  out[8 + data.length] = (crc >>> 24) & 255;
  out[9 + data.length] = (crc >>> 16) & 255;
  out[10 + data.length] = (crc >>> 8) & 255;
  out[11 + data.length] = crc & 255;
  return out;
};

const zlibStore = (data) => {
  const blocks = [];
  let offset = 0;
  if (!data.length) {
    blocks.push(1, 0, 0, 255, 255);
  }
  while (offset < data.length) {
    const remaining = data.length - offset;
    const length = Math.min(65535, remaining);
    const final = offset + length >= data.length ? 1 : 0;
    const complement = (~length) & 0xFFFF;
    blocks.push(final, length & 255, (length >> 8) & 255, complement & 255, (complement >> 8) & 255);
    for (let index = 0; index < length; index += 1) blocks.push(data[offset + index]);
    offset += length;
  }
  const checksum = adler32(data);
  const out = new Uint8Array(2 + blocks.length + 4);
  out[0] = 0x78;
  out[1] = 0x01;
  blocks.forEach((byte, index) => {
    out[2 + index] = byte;
  });
  const tail = 2 + blocks.length;
  out[tail] = (checksum >>> 24) & 255;
  out[tail + 1] = (checksum >>> 16) & 255;
  out[tail + 2] = (checksum >>> 8) & 255;
  out[tail + 3] = checksum & 255;
  return out;
};

const encodePng = (rgba, width, height) => {
  const stride = width * 4;
  const raw = new Uint8Array(height * (stride + 1));
  for (let y = 0; y < height; y += 1) {
    const dest = y * (stride + 1);
    raw[dest] = 0;
    raw.set(rgba.subarray(y * stride, (y + 1) * stride), dest + 1);
  }
  const ihdr = new Uint8Array(13);
  ihdr[0] = (width >>> 24) & 255;
  ihdr[1] = (width >>> 16) & 255;
  ihdr[2] = (width >>> 8) & 255;
  ihdr[3] = width & 255;
  ihdr[4] = (height >>> 24) & 255;
  ihdr[5] = (height >>> 16) & 255;
  ihdr[6] = (height >>> 8) & 255;
  ihdr[7] = height & 255;
  ihdr[8] = 8;
  ihdr[9] = 6;
  const signature = Uint8Array.from(PNG_SIGNATURE);
  const chunks = [signature, pngChunk('IHDR', ihdr), pngChunk('IDAT', zlibStore(raw)), pngChunk('IEND', new Uint8Array(0))];
  let size = 0;
  chunks.forEach((chunk) => {
    size += chunk.length;
  });
  const png = new Uint8Array(size);
  let cursor = 0;
  chunks.forEach((chunk) => {
    png.set(chunk, cursor);
    cursor += chunk.length;
  });
  return png;
};

const isMarginPixel = (red, green, blue, alpha) => (
  alpha < MARGIN_ALPHA || (red >= MARGIN_WHITE && green >= MARGIN_WHITE && blue >= MARGIN_WHITE)
);

const contentBounds = (rgba, width, height) => {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = ((y * width) + x) * 4;
      if (isMarginPixel(rgba[index], rgba[index + 1], rgba[index + 2], rgba[index + 3])) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return { left: 0, top: 0, width, height };
  return { left: minX, top: minY, width: (maxX - minX) + 1, height: (maxY - minY) + 1 };
};

const cropRgba = (rgba, width, bounds) => {
  const cropped = new Uint8Array(bounds.width * bounds.height * 4);
  for (let y = 0; y < bounds.height; y += 1) {
    const src = (((bounds.top + y) * width) + bounds.left) * 4;
    cropped.set(rgba.subarray(src, src + (bounds.width * 4)), y * bounds.width * 4);
  }
  return cropped;
};

// Returns the PNG to embed and the pixel size that fixes the aspect ratio.
// A margin of transparent or near-white pixels is removed. The original
// base64 is kept when there is nothing to trim or the file cannot be read.
export const prepareExcelLogo = (base64) => {
  const original = String(base64 || '');
  // Decode inside the try: a malformed string must fall back, not throw
  // out of the export.
  let bytes = new Uint8Array(0);
  try {
    bytes = bytesFromBase64(original);
    const image = parsePng(bytes);
    const bounds = contentBounds(image.rgba, image.width, image.height);
    if (bounds.left === 0 && bounds.top === 0 && bounds.width === image.width && bounds.height === image.height) {
      return { base64: original, width: image.width, height: image.height };
    }
    const cropped = cropRgba(image.rgba, image.width, bounds);
    return {
      base64: bytesToBase64(encodePng(cropped, bounds.width, bounds.height)),
      width: bounds.width,
      height: bounds.height
    };
  } catch (error) {
    console.warn('Excel logo could not be cropped; using the original image.', error);
    const size = bytes.length >= 24 ? { width: readU32(bytes, 16), height: readU32(bytes, 20) } : null;
    const png = bytes.length >= 8 && PNG_SIGNATURE.every((byte, index) => bytes[index] === byte);
    const edge = (value) => (value >= 1 && value <= MAX_PNG_EDGE ? value : 0);
    const width = png && size ? edge(size.width) : 0;
    const height = png && size ? edge(size.height) : 0;
    return {
      base64: original,
      width: width || 1,
      height: height || 1
    };
  }
};

export const excelLogoSize = (width, height) => {
  const safeHeight = height > 0 ? height : 1;
  const safeWidth = width > 0 ? width : 1;
  const logoHeight = LOGO_TARGET_HEIGHT;
  const logoWidth = Math.max(1, Math.round(logoHeight * (safeWidth / safeHeight)));
  return { width: logoWidth, height: logoHeight };
};

export const EXCEL_LOGO_ROW_POINTS = 136;
export const EXCEL_LOGO_TARGET_HEIGHT = LOGO_TARGET_HEIGHT;
